import { animate, style, transition, trigger } from '@angular/animations';
import {
	ChangeDetectionStrategy,
	Component,
	HostBinding,
	Input,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { first, switchMap, tap } from 'rxjs/operators';

import { NotificationType } from 'application/notification/model/notification.type';
import { DocumentViewerDialogComponent } from 'dialog/document-viewer-dialog/document-viewer-dialog.component';
import { MeetingAlternativeDialogComponent } from 'dialog/meeting-alternative-dialog/meeting-alternative-dialog.component';
import { MeetingRequestDialogComponent } from 'dialog/meeting-request-dialog/meeting-request-dialog.component';
import { MeetingRequestDialogData } from 'dialog/meeting-request-dialog/meeting-request-dialog.data';
import { SetModuleCommentDialogData } from 'dialog/set-module-comment-dialog/set-module-comment-dialog.data';
import { SetModuleRatingDialogComponent } from 'dialog/set-module-rating-dialog/set-module-rating-dialog.component';
import { INotification } from 'domain/notification/notification';
import { UserApplicationPort } from 'user/port/user-application.port';
import { IUser } from 'user/user';

import { SidebarNotificationController } from './sidebar-notification.controller';

@Component({
	selector: 'spr-sidebar-notification',
	templateUrl: './sidebar-notification.component.html',
	host: {
		class:
			'p-background-color-primary-pastel p-border-color-primary-pastel flex border relative items-center flex-row border-solid group rounded py-1 px-3 space-x-3',
	},
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [SidebarNotificationController],
	animations: [
		trigger('inOut', [
			transition(':enter', [
				style({ transform: 'translateX(-100%)' }),
				animate('250ms', style({ transform: 'translateX(0)' })),
			]),
			transition(':leave', [
				style({ transform: 'translateX(0)', height: '*' }),
				animate('250ms', style({ transform: 'translateX(-100%)', height: 0 })),
			]),
		]),
	],
})
export class SidebarNotificationComponent {
	@HostBinding('@inOut') public inOut = true;

	@Input() public locale: string;
	@Input() public isFirst: boolean;
	@Input() public notification: INotification;

	public readonly type = NotificationType;

	constructor(
		private _router: Router,
		private _dialogService: DialogService,
		private _translateService: TranslateService,
		private _confirmationService: ConfirmationService,
		private _userApplicationPort: UserApplicationPort,
		public controller: SidebarNotificationController
	) {}

	public onNotificationActionClick(sessionUser: IUser) {
		switch (this.notification.type) {
			case NotificationType.MEETING_ALTERNATIVE:
				if (this.notification.isSource(sessionUser)) {
					this._confirmationService.confirm({
						message: this._translateService.instant(
							'presentation.app.sidebar.notifications.notification.meetingRequest.cancel.message'
						),
						accept: () =>
							this.controller.cancelMeetingRequest(this.notification.id).subscribe(),
					});
				} else {
					this._dialogService.open(MeetingAlternativeDialogComponent, {
						header: this._translateService.instant(
							'presentation.app.common.dialog.meetingAlternative.title'
						),
						data: {
							notificationId: this.notification.id,
						} as MeetingRequestDialogData,
						dismissableMask: true,
						width: '700px',
					});
				}
				break;
			case NotificationType.MEETING_REQUEST:
				if (this.notification.isSource(sessionUser)) {
					this._confirmationService.confirm({
						message: this._translateService.instant(
							'presentation.app.sidebar.notifications.notification.meetingRequest.cancel.message'
						),
						accept: () =>
							this.controller.cancelMeetingRequest(this.notification.id).subscribe(),
					});
				} else {
					this._dialogService.open(MeetingRequestDialogComponent, {
						header: this._translateService.instant(
							'presentation.app.common.dialog.meetingRequest.title'
						),
						data: {
							notificationId: this.notification.id,
						} as MeetingRequestDialogData,
						dismissableMask: true,
						width: '700px',
					});
				}
				break;
			case NotificationType.DOCUMENT_ADDED:
			case NotificationType.BASIC_ASSESSMENT_DOCUMENT_GENERATED:
			case NotificationType.DETAILED_ASSESSMENT_DOCUMENT_GENERATED:
				this.notification.document$
					.pipe(
						first(),
						switchMap((document) =>
							document.localization$.pipe(
								first(),
								tap((loc) => {
									this._dialogService.open(DocumentViewerDialogComponent, {
										dismissableMask: true,
										data: { url: loc.url },
										header: loc.name,
										width: '90%',
									});
								})
							)
						)
					)
					.subscribe();

				this.controller.markNotificationRead(this.notification);
				break;
			case NotificationType.MODULE_DONE:
				if (this.notification.sourceUserId) {
					this._router.navigate([
						'/activity',
						this.notification.moduleId,
						'user',
						this.notification.sourceUserId,
					]);
				} else {
					this._dialogService.open(SetModuleRatingDialogComponent, {
						header: this._translateService.instant(
							'presentation.app.common.dialog.userModuleRating.title'
						),
						data: {
							moduleId: this.notification.moduleId,
							coacheeId: this.notification.targetUserId,
						} as SetModuleCommentDialogData,
						dismissableMask: true,
						width: '700px',
					});
				}
				this.controller.markNotificationRead(this.notification);
				break;
			case NotificationType.MODULE_STARTED:
				this._router.navigate([
					'/activity',
					this.notification.moduleId,
					'user',
					this.notification.sourceUserId,
				]);
				this.controller.markNotificationRead(this.notification);
				break;
			case NotificationType.MODULE_ADDED:
			case NotificationType.MODULE_COMMENTED:
				this._router.navigate([
					'/activity',
					this.notification.moduleId,
					'user',
					this.notification.targetUserId,
				]);
				this.controller.markNotificationRead(this.notification);
				break;
			default:
				this.controller.markNotificationRead(this.notification);
				break;
		}
	}

	public onNotificationCloseClick() {
		this.controller.markNotificationRead(this.notification);
	}
}
